import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getAdConfig, isInPageEligibleRoute, isVignetteEligibleRoute } from '../config/ads';
import { advertisingConsentGranted, CONSENT_EVENT } from '../lib/consent';
import {
  getAdvertisingLockState,
  loadMonetagScript,
  lockAdvertising,
  subscribeAdvertisingLocks,
  unlockAdvertising,
} from '../lib/monetag';

const SESSION_KEY = 'solargabay.monetag.session.v1';
type SessionState = {
  startedAt: number;
  pageViews: number;
  vignetteCount: number;
  lastVignetteAt: number;
};

const event = (name: string, detail: Record<string, string | number> = {}) =>
  window.gtag?.('event', name, detail);

const readSession = (): SessionState => {
  const fallback = { startedAt: Date.now(), pageViews: 0, vignetteCount: 0, lastVignetteAt: 0 };
  try {
    const value = JSON.parse(
      sessionStorage.getItem(SESSION_KEY) ?? 'null',
    ) as Partial<SessionState> | null;
    return value && typeof value.startedAt === 'number' ? { ...fallback, ...value } : fallback;
  } catch {
    return fallback;
  }
};

const writeSession = (state: SessionState) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    /* session rules fail closed */
  }
};

export function MonetagController() {
  const location = useLocation();

  useEffect(() => {
    const config = getAdConfig();
    const state = readSession();
    state.pageViews += 1;
    writeSession(state);
    let timer = 0;
    let cooldownTimer = 0;
    let cancelled = false;

    const tryInPage = () => {
      if (cancelled || !config.formats.inPagePush.enabled) return;
      if (!isInPageEligibleRoute(location.pathname))
        return event('ad_suppressed_route', { format: 'in_page_push' });
      if (document.visibilityState !== 'visible')
        return event('ad_suppressed_hidden_tab', { format: 'in_page_push' });
      if (!advertisingConsentGranted())
        return event('ad_suppressed_consent', { format: 'in_page_push' });
      const lockState = getAdvertisingLockState();
      const remaining = config.interactionCooldownMs - (Date.now() - lockState.lastUnlockAt);
      if (lockState.reasons.length || remaining > 0) {
        event('ad_suppressed_interaction', { format: 'in_page_push' });
        cooldownTimer = window.setTimeout(tryInPage, Math.max(remaining, 500));
        return;
      }
      event('ad_format_eligible', { format: 'in_page_push' });
      const inPageScript = document.querySelector<HTMLScriptElement>(
        `script[src="${config.formats.inPagePush.scriptUrl}"][data-zone="${config.formats.inPagePush.zoneId}"]`,
      );
      if (!inPageScript) {
        event('ad_script_requested', { format: 'in_page_push' });
        void loadMonetagScript({
          id: 'monetag-inpage-push',
          zoneId: config.formats.inPagePush.zoneId,
          src: config.formats.inPagePush.scriptUrl,
        }).then((loaded) =>
          event(loaded ? 'ad_script_loaded' : 'ad_script_failed', { format: 'in_page_push' }),
        );
        return;
      }
      event('ad_script_loaded', { format: 'in_page_push' });
    };

    timer = window.setTimeout(tryInPage, config.formats.inPagePush.delayMs);
    const unsubscribe = subscribeAdvertisingLocks(() => {
      window.clearTimeout(cooldownTimer);
      tryInPage();
    });
    const consentListener = () => tryInPage();
    const visibilityListener = () => {
      if (document.visibilityState === 'visible') tryInPage();
    };
    window.addEventListener(CONSENT_EVENT, consentListener);
    document.addEventListener('visibilitychange', visibilityListener);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.clearTimeout(cooldownTimer);
      unsubscribe();
      window.removeEventListener(CONSENT_EVENT, consentListener);
      document.removeEventListener('visibilitychange', visibilityListener);
    };
  }, [location.pathname]);

  useEffect(() => {
    const config = getAdConfig();
    if (!config.formats.vignette.enabled || !isVignetteEligibleRoute(location.pathname)) return;
    const session = readSession();
    if (session.pageViews < config.formats.vignette.minimumPageViews) return;
    const remainingSessionAge = Math.max(
      0,
      config.formats.vignette.minimumSessionAgeMs - (Date.now() - session.startedAt),
    );
    const timer = window.setTimeout(() => {
      const current = readSession();
      if (
        Date.now() - current.lastVignetteAt < config.formats.vignette.cooldownMinutes * 60000 ||
        getAdvertisingLockState().reasons.length > 0 ||
        !advertisingConsentGranted()
      )
        return;
      const loadVignette = window.__SOLARGABAY_LOAD_MONETAG_VIGNETTE__;
      if (!loadVignette) {
        event('ad_script_failed', { format: 'vignette' });
        return;
      }
      const reloadProviderTag = current.vignetteCount > 0;
      current.vignetteCount += 1;
      current.lastVignetteAt = Date.now();
      writeSession(current);
      event('vignette_eligible');
      event('ad_script_requested', { format: 'vignette' });
      void loadVignette(reloadProviderTag).then((loaded) =>
        event(loaded ? 'ad_script_loaded' : 'ad_script_failed', { format: 'vignette' }),
      );
    }, remainingSessionAge);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const element = event.target as HTMLElement;
      if (element.matches('input, textarea, select, [contenteditable="true"]'))
        lockAdvertising('form-input');
    };
    const onFocusOut = () => unlockAdvertising('form-input');
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  return null;
}
